import { useRequestHeaders, useRoute } from '#imports'
import _ from 'lodash'
import useDisplayMode from '~/composables/useDisplayMode'
import { DEFAULT_DISPLAY_MODE } from '~/types/constants'
import { DriveKind, FileOrder, OrderTypeQueryName, RouteQueryKey } from '~/types/enum'
import { isAudio, isVideo } from '~/utils'
import { formatErrorString } from '~/utils/formateError'

export type OrderType = Exclude<FileOrder, FileOrder.TYPE_DESC>

export const isNaturalSort = (orderType: OrderType): orderType is FileOrder.NAME_ASC | FileOrder.NAME_DESC =>
  [FileOrder.NAME_ASC, FileOrder.NAME_DESC].includes(orderType)

export const isValidOrderType = (val: unknown): val is OrderType => {
  if (typeof val !== 'string') return false

  const orderTypes = Object.values(FileOrder).filter((el): el is OrderType => el !== FileOrder.TYPE_DESC)
  return orderTypes.includes(val as OrderType)
}
const getOrderFromQuery = () => {
  const query = useRoute().query

  if (query[RouteQueryKey.ORDER_TYPE]) {
    const val = query[RouteQueryKey.ORDER_TYPE]
    const temp = Object.entries(OrderTypeQueryName).find(([, v]) => v === val) || []
    // @ts-expect-error 这里从外部传入的排序
    if (temp[0]) return FileOrder[temp[0]]
  }

  return ''
}

export const getOrderTypeFromStorage = async (checkQuery?: boolean): Promise<OrderType> => {
  const { orderType } = useDisplayMode()
  if (checkQuery) {
    const orderTypeInQuery = getOrderFromQuery()
    if (orderTypeInQuery) {
      // 同时更新 cookie
      const validOrderType = isValidOrderType(orderTypeInQuery) ? orderTypeInQuery : DEFAULT_DISPLAY_MODE.orderType
      orderType.value = validOrderType
      return validOrderType
    }
  }
  const orderTypeInStorage = orderType.value

  if (!isValidOrderType(orderTypeInStorage)) return DEFAULT_DISPLAY_MODE.orderType
  return orderTypeInStorage
}

export const paramOrder = (orderType: OrderType) =>
  (isNaturalSort(orderType) ? FileOrder.TYPE_DESC : orderType) as Exclude<
    FileOrder,
    FileOrder.NAME_ASC | FileOrder.NAME_DESC
  >

// 使用浏览器设置的语言进行自然排序
// 在 SSR 通过 HTTP Header 获取用户浏览器的 language
const getNavigatorLanguage = () => {
  if (import.meta.server) {
    const key = 'accept-language'
    const acceptLanguage = useRequestHeaders([key])[key]

    if (acceptLanguage) {
      const preferredLanguage = acceptLanguage.split(',')[0].split(';')[0] // 'en' or 'en;q=1'
      return preferredLanguage || 'en'
    }
    return 'en'
  }
  return navigator.language || 'en'
}

// 获取自然排序的比较函数
const getCompare = () => {
  let compareCollator = null
  try {
    compareCollator = new Intl.Collator(getNavigatorLanguage(), { numeric: true, sensitivity: 'base' })
  } catch (err) {
    formatErrorString(err as Error, { locale: getNavigatorLanguage() })
    compareCollator = new Intl.Collator('en', { numeric: true, sensitivity: 'base' })
  }
  return (x: string, y: string) => compareCollator.compare(x, y)
}
export const isFile = (val?: DriveKind): boolean => val === DriveKind.FILE

/* 将文件夹与文件分组，文件夹在前，文件在后, My Pack 文件夹置顶 */
export const groupByKind = <T extends { folder_type: PPFile['folder_type']; kind: DriveKind }>(list: T[]): T[] => {
  if (!useDisplayMode().pinFoldersTop.value) return list

  const myPackFolder = []
  const folders = []
  const files = []

  let i = 0
  const length = list.length
  while (i < length) {
    if (list[i].folder_type === 'DOWNLOAD') {
      myPackFolder.push(list[i])
    } else if (isFile(list[i].kind)) {
      files.push(list[i])
    } else {
      folders.push(list[i])
    }
    i += 1
  }

  return myPackFolder.concat(folders).concat(files)
}

export const naturalSort = (
  list: PPFile[],
  orderType: FileOrder.NAME_ASC | FileOrder.NAME_DESC = FileOrder.NAME_ASC,
) => {
  if (!list.length) return list
  const originList = [...(list || [])]

  const compare = getCompare()

  const sortedList = groupByKind(originList.sort((a, b) => compare(a.name, b.name)) || [])

  return (orderType === FileOrder.NAME_DESC ? sortedList.slice().reverse() : sortedList).filter(el => !!el)
}

export const fileSizeSort = (
  list: PPFile[],
  orderType: Extract<FileOrder, FileOrder.SIZE_ASC | FileOrder.SIZE_DESC> = FileOrder.SIZE_ASC,
) => {
  if (!list.length) return list
  const originList = [...(list || [])]

  const compare = getCompare()

  originList.sort((a, b) => compare(a.size, b.size))

  return orderType === FileOrder.SIZE_DESC ? originList.slice().reverse() : originList
}

/** 判断是否是剧集文件 */
export const isEpisodeShareFiles = (files: PPFile[]) => {
  if (!files.length || files.length >= 100) return false
  const videoFiles = files.filter(
    el => isVideo(el.file_extension, el.mime_type) || isAudio(el.file_extension, el.mime_type),
  )
  if (videoFiles.length <= 1) return false

  /*
   * 判断规则：
   * 1.过滤掉每个音视频文件名中数字，剩余字符数仍 > 2 的文件
   * 2.每个文件取文件名的前 80% 的字符
   * 3.若有 80%的文件数，存在内容想同，则认为是剧集文件
   */
  const fileNames = videoFiles
    .map(el => el.name.replace(/[0-9]/g, ''))
    .filter(name => name && name.length > 2)
    .map(name => name.slice(0, Math.floor(name.length * 0.8)))

  if (fileNames.length <= 1) return false

  const length80 = Math.floor(fileNames.length * 0.8)
  const isSimilarNames = (baseName: string, index: number) =>
    fileNames.filter((name, i) => i !== index && name === baseName).length >= length80
  // 循环名称列表，只要有一个满足条件，则判断为剧集文件

  for (let i = 0; i < fileNames.length; i++) {
    if (isSimilarNames(fileNames[i], i)) return true
  }
  return false
}

// @ts-expect-error
export const removeUrlKeyFromFiles = <T extends { files: PPFile[] }>({ files, ...rest }: T): T => ({
  ...rest,
  files:
    files?.map(el => ({
      ...el,
      params: _.omit(el.params, 'url'),
    })) || [],
})
